import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import SocialFollow from '../components/SocialFollow';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    userType: 'brand',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 py-20">
        <div className="mx-auto max-w-6xl text-center">
          <h1 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent md:text-6xl">
            Get in Touch
          </h1>
          <p className="mx-auto max-w-4xl text-xl leading-relaxed text-gray-400 md:text-2xl">
            We'd love to hear from you. Whether you have questions, feedback, or need support,
            our team is here to help you succeed.
          </p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 text-white">Send us a message</h2>
              
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl">✓</span>
                  </div>
                  <h3 className="text-2xl font-bold mb-2 text-white">Thank you!</h3>
                  <p className="text-gray-400 mb-6">
                    Your message has been sent. We'll get back to you within 24 hours.
                  </p>
                  <button 
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-2 bg-gradient-to-r from-violet-600 to-blue-500 text-white rounded-lg hover:opacity-90 transition"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form action={`mailto:i@kol.my?subject=${encodeURIComponent(formData.subject || 'Contact from Website')}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company}\nType: ${formData.userType}\n\nMessage:\n${formData.message}`)}`} method="POST" encType="text/plain" className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Company
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                        placeholder="Your Company"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        I'm a... *
                      </label>
                      <select
                        name="userType"
                        required
                        value={formData.userType}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-[#1a1a2e] border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white"
                        style={{ backgroundColor: '#1a1a2e' }}
                      >
                        <option value="brand" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Brand / Company</option>
                        <option value="kol" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>KOL / Influencer</option>
                        <option value="agency" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Agency</option>
                        <option value="media" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Media / Press</option>
                        <option value="other" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Subject *
                    </label>
                    <select
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#1a1a2e] border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white"
                      style={{ backgroundColor: '#1a1a2e' }}
                    >
                      <option value="" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Select a topic</option>
                      <option value="Sales Inquiry" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Sales Inquiry</option>
                      <option value="Technical Support" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Technical Support</option>
                      <option value="Partnership Opportunity" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Partnership Opportunity</option>
                      <option value="Product Feedback" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Product Feedback</option>
                      <option value="Press Inquiry" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Press Inquiry</option>
                      <option value="Other" style={{ backgroundColor: '#1a1a2e', color: 'white' }}>Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                      placeholder="Tell us how we can help you..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-4 bg-gradient-to-r from-violet-600 to-blue-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-white">Contact Information</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">✉️</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Email</h4>
                      <p className="text-gray-400">i@kol.my</p>
                      <p className="text-sm text-gray-500">We respond within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">📞</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Phone</h4>
                      <p className="text-gray-400">+60 3-1234 5678</p>
                      <p className="text-sm text-gray-500">Mon-Fri, 9am-6pm MYT</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">📍</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-white mb-1">Office</h4>
                      <p className="text-gray-400">
                        Suite 12.03, Level 12<br />
                        Wisma UOA Pantai<br />
                        59200 Kuala Lumpur<br />
                        Malaysia
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <SocialFollow />

              <div className="bg-yellow-500/10 border-l-4 border-yellow-500 rounded-lg p-6">
                <h4 className="font-bold text-yellow-200 mb-2">Quick Response</h4>
                <p className="text-gray-400 text-sm leading-relaxed">
                  For urgent matters, our support team is available via live chat during business hours.
                </p>
              </div>
            </div>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={3} className="px-6 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-4xl font-bold text-transparent">
            Need Quick Answers?
          </h2>
          <p className="mb-8 text-xl text-gray-400">
            Check our frequently asked questions for instant help with common queries.
          </p>
          <Link
            to="/faq"
            className="inline-block rounded-lg bg-gradient-to-r from-violet-600 to-blue-500 px-8 py-4 text-lg font-semibold text-white transition-all hover:shadow-xl hover:shadow-violet-500/30"
          >
            View FAQ
          </Link>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Contact;
